import type { MergeElementProps } from '@mogul/shared';
import clsx from 'clsx';
import { createElement, forwardRef, useState } from 'react';

import type { InputProps } from '../input';
import { Input } from '../input';
import styles from './InputField.module.css';

const useUniqueId = () =>
  useState(() => Math.random().toString(32).slice(2))[0];

export interface InputFieldRenderProps {
  className?: string;
}

export interface InputRenderProps {
  id?: string;
  'aria-labelledby'?: string;
  disabled?: boolean;
}

export type InputFieldProps<
  T extends React.ElementType<InputFieldRenderProps> = 'div',
> = MergeElementProps<
  T,
  {
    /**
     * Override the root element type.
     *
     * @default div
     */
    Component?: T;

    /**
     * Accessible field label rendered above the input element.
     */
    label: React.ReactNode;

    /**
     * Message (error or otherwise) rendered below the input element
     */
    helperText?: React.ReactNode;

    /**
     * Display the form field in an error state.
     */
    error?: boolean;

    /**
     * Disable the inner input element while changing the visual style of the
     * field label and helper text to match.
     */
    disabled?: boolean;

    /**
     * Visually hide the field label without removing, preserving field a11y.
     */
    hideLabel?: boolean;

    /**
     * Props applied to the inner `Input` component.
     */
    InputProps?: InputProps;
  }
>;

/**
 * Renders a form input with label above and helper text below
 */
export const InputField = forwardRef(
  <T extends React.ElementType<InputFieldRenderProps> = 'div'>(
    props: InputFieldProps<T>,
    ref: React.ForwardedRef<React.ElementRef<T>>,
  ) => {
    const labelId = useUniqueId();
    const inputId = useUniqueId();

    const {
      Component = 'div',
      className,
      label,
      helperText,
      error,
      disabled,
      hideLabel,
      InputProps: { id = inputId, ...baseInputProps } = {},
      ...baseProps
    } = props;

    const renderProps: InputFieldRenderProps = {
      ref,
      className: clsx(className, styles.root, {
        [styles.error]: error,
        [styles.disabled]: disabled,
      }),
      ...baseProps,
    };

    return createElement(
      Component,
      renderProps,
      <label
        className={styles.label}
        id={labelId}
        htmlFor={inputId}
        hidden={hideLabel}
      >
        {label}
      </label>,
      <Input
        id={id}
        aria-labelledby={labelId}
        disabled={disabled}
        {...baseInputProps}
      />,
      <div className={styles.helperText}>{helperText}</div>,
    );
  },
);
